import { useControllerConnectionStatus } from '../../Hooks/useControllerConnectionStatus';
import React, { useEffect, useState, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Box, Button, IconButton, Grid, makeStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import MuiSwitch from '../../FormWidgets/SettingsSwitch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@material-ui/icons/Edit';
import { Collapse } from '@mui/material';
import DialogWithEnterAction from '../../components/DialogWithEnterAction';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

import { GetSaveButtonStyle as saveButtonStyle } from '../../Styles/styles';

import getStateManager from '../../StateManager';

import { SettingActions } from '../../components';

import { SettingGroupGrid, ValueBox } from '../../FormWidgets/SettingGroup';
import { GenericSelect, MultiIOSelect, MultiIOSelectIndex } from '../../FormWidgets/Select.js';
import Desc from '../../FormWidgets/Description';
import { EditableLabel } from '../../FormWidgets/EditableLabel';

import { ExpandMore } from '../../Utils/Expand';
import { LoadSettingsDialog } from '../../Utils/load_settings';
import { settingControls, useSavedSettings, useSettingsReadback, writeTempSettings } from '../settingFunctions';
import { useReceivedSettings } from '../../Hooks/useTabController';
import { settingsLeafPaths, createNestedSettingsDraft, createSettingsDraftStore, restoreSettingsDraft } from '../../Utils/settings_draft';
import {
  areValveRouteFunctionSetsSupported,
  getMultiIoIndexConfig,
  getMultiIoIndexCount,
  supportsMultiIoFunctionSets
} from '../../Utils/multi_io_settings';

import _, { parseInt } from 'lodash';

import {
  GetIndexMapping,
  multiIOMapping,
  statusColors
} from '../../Constants/constants';

const useStyles = makeStyles((theme) => ({
  box: {
    backgroundColor: theme.palette.innerContainer2.background
  },
  icon: {
    color: theme.palette.text.primary
  }
}));

export default function MultiIOUserFunctionSet({ ...other }) {
  let { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const tab_id = 'user_func_sets';
  let params = useParams();

  let stateManager = getStateManager();
  const { controller, parent } = stateManager.getControllerForTab(params);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const [values, setValues] = useState(_.cloneDeep(controller.settings[tab_id]));
  const [dialog, setDialog] = useState({ open: false, member: undefined, index: -1, id: '' });
  const draft = useMemo(() => {
    const nested = createNestedSettingsDraft(createSettingsDraftStore(controller, tab_id,
      () => writeTempSettings(controller)));
    return { ...nested, restore: (baseline) => {
      const saved = nested.read();
      const restored = restoreSettingsDraft(baseline, saved);
      Object.entries(saved).forEach(([id, entry]) => {
        const members = entry?.members;
        if (!saved._deleted_indicies?.[`${id}.members`] && Array.isArray(members) &&
            Array.from(members).every((member) => member?.function !== undefined && member?.source_zone !== undefined))
          restored[id].members = _.cloneDeep(members);
      });
      return restored;
    } };
  }, [controller]);
  const received = useReceivedSettings(controller, tab_id);
  const readback = useSettingsReadback(controller, tab_id, received, values, setValues, {
    draft,
    canRetain: useSnapshot(controller).access_rights?.multi_io?.write === true
  });
  settingsLeafPaths(values).forEach(readback.retainSetting);
  Object.keys(values ?? {}).forEach((id) => readback.retainSetting(`${id}.members`));
  const controls = settingControls(controller, parent, tab_id, values, setValues, {
    ...readback,
    draft,
    canEdit: () =>
      controller.access_rights?.multi_io?.read === true &&
      controller.access_rights?.multi_io?.write === true
  });
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(
    controller,
    controls,
    tab_id
  );

  const controllerSnapshot = useSnapshot(controller);
  const access = controllerSnapshot.access_rights?.multi_io;
  // Controller readback the inputs are compared against.
  const receivedFuncSets = useSnapshot(controller.settings)[tab_id];
  const write_access = access?.write === true;

  const EnableNextFreeFunctionSet = () => {
    let next_free = '';
    let highest_enabled = 0;
    Object.entries(values ?? {}).map(([id, func_set]) => {
      if (!func_set.enabled && next_free === '') {
        next_free = id;
      }
      if (func_set.enabled && func_set.set_index > highest_enabled) {
        highest_enabled = func_set.set_index;
      }
    });
    if (next_free !== '') {
      controls.setValue(
        [`${next_free}.enabled`, `${next_free}.set_index`],
        [true, highest_enabled + 1]
      );
    }
  };

  const DisableFunctionSet = (key) => {
    let set_index = 0;
    Object.entries(values ?? {})
      .sort(([ka, va], [kb, vb]) => va.set_index > vb.set_index)
      .map(([id, func_set]) => {
        // Reset element to default values
        if (id === key) {
          controls.setValue(
            [
              `${id}.name`,
              `${id}.enabled`,
              `${id}.set_index`,
              `${id}.logic_operation`,
              `${id}.target_zone`,
              `${id}.member_count`,
              `${id}.members`
            ],
            ['', false, 0, 0, 0, 0, []]
          );
          set_index = func_set.set_index;
        }
        // Decrease `set_index` of all following elements
        if (func_set.set_index > set_index) {
          controls.setValue(`${id}.set_index`, func_set.set_index - 1);
        }
      });
  };

  const transform_before_save = () => {
    return Object.values(values ?? {});
  };

  const saveSettings = () => {
    if (!write_access) return false;
    if (!areValveRouteFunctionSetsSupported(controller, parent, values, controller.settings[tab_id])) {
      enqueueSnackbar(t('valve_irrigation_route_unavailable'), { variant: 'error' });
      return false;
    }
    return controls.saveSettings(transform_before_save, false);
  };

  if (access?.read !== true || !supportsMultiIoFunctionSets(controllerSnapshot)) return null;
  return (
    <React.Fragment>
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      <EditMemberDialog
        dialog={dialog}
        setDialog={setDialog}
        controller={controller}
        controls={controls}
        write_access={write_access}
      />

      {write_access && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={saveSettings}
        />
      )}

      {Object.entries(values ?? {})
        .sort(([ka, va], [kb, vb]) => va.set_index > vb.set_index)
        .filter(([_, func_set]) => func_set.enabled)
        .map(([id, func_set]) => (
          <FunctionSet
            expand_id={`function_set_${id}`}
            id={id}
            func_set={func_set}
            controllerFuncSet={receivedFuncSets?.[id]}
            values={values}
            controls={controls}
            controller={controller}
            write_access={write_access}
            setDialog={setDialog}
            Disable={DisableFunctionSet}
          />
        ))}
      <Button color="primary" disabled={!write_access} onClick={() => EnableNextFreeFunctionSet()}>
        {t('add_function_set')}
      </Button>

      {write_access && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={saveSettings}
        />
      )}
    </React.Fragment>
  );
}

function FunctionSet({
  expand_id,
  id,
  values,
  func_set,
  controllerFuncSet,
  controls,
  controller,
  write_access,
  setDialog,
  Disable
}) {
  let { t } = useTranslation();
  const classes = useStyles();

  // Initialize state from localStorage or default value
  const [expand, setExpand] = useState(() => {
    if (!expand_id) return controls.hasChanged('id');
    const savedState = localStorage.getItem(`expandable_${expand_id}`);
    return savedState !== null ? savedState === 'true' : controls.hasChanged('id');
  });

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (expand_id) {
      localStorage.setItem(`expandable_${expand_id}`, expand.toString());
    }
  }, [expand, expand_id]);

  useEffect(() => {
    setExpand(expand || controls.hasChanged('id'));
  }, [values]);

  const AddMember = () => {
    let new_member = {
      function: 19,
      func_index: 0,
      source_zone: 0xff,
      invert_logic: false,
      enabled: true
    };
    let updated_members = [...func_set.members];
    updated_members.push(new_member);
    controls.setValue(
      [`${id}.member_count`, `${id}.members`],
      [func_set.member_count + 1, updated_members]
    );
    setDialog({ open: true, member: new_member, index: func_set.member_count - 1, id: id });
  };

  const RemoveMember = (index) => {
    let updated_members = func_set.members.filter((_, i) => i !== index);
    controls.setValue(
      [`${id}.member_count`, `${id}.members`],
      [func_set.member_count - 1, updated_members]
    );
    controls.addDeletedArrayIndex(index, `${id}.members`);
  };

  return (
    <SettingGroupGrid label="" fullWidth={true}>
      {/* Close a previously opened name dialog when editing permission changes. */}
      <EditableLabel
        key={String(write_access)}
        id={`${id}.name`}
        controls={controls}
        value={func_set.name}
        handleInput={(_, evt, value) => controls.setValue(`${id}.name`, value)}
        no_label={true}
        placeholder="function_set_name"
        disabled={!write_access}
      />

      <div style={{ textAlign: 'center', marginTop: '-5em', marginLeft: '45%', width: '10%' }}>
        <ExpandMore
          expand={expand}
          onClick={() => setExpand(!expand)}
          aria-expanded={expand[func_set.set_index]}
          aria-label="show more"
        >
          <ExpandMoreIcon fontSize="large" className={classes.icon} />
        </ExpandMore>
      </div>

      <Collapse in={expand} timeout="auto" unmountOnExit>
        <Grid container spacing={1} columns={{ xs: 6, md: 12 }}>
          <Grid item xs={12} md={6}>
            <ValueBox controller={controller} id="user_func_sets_target_zone">
              <Typography gutterBottom>
                <Desc id="target_zone" />
              </Typography>
              <GenericSelect
                disabled={!write_access}
                onChange={(evt) => controls.setValue(`${id}.target_zone`, evt.target.value)}
                values={Object.values(controller.zones)
                  .map((z) => ({ value: parseInt(z.id), label: z.name }))
                  .concat([
                    { value: 0xfe, label: t('global') },
                    { value: 0xff, label: t('undefined') }
                  ])}
                value={func_set.target_zone}
                controller_value={controllerFuncSet?.target_zone}
              />
            </ValueBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <ValueBox controller={controller} id="user_func_sets_logic_operation">
              <Typography gutterBottom>
                <Desc id="logic_operation" />
              </Typography>
              <GenericSelect
                disabled={!write_access}
                onChange={(evt) => controls.setValue(`${id}.logic_operation`, evt.target.value)}
                values={[
                  { value: 0, label: '&&' },
                  { value: 1, label: '||' }
                ]}
                value={func_set.logic_operation}
                controller_value={controllerFuncSet?.logic_operation}
              />
            </ValueBox>
          </Grid>

          <Grid item xs={12} md={12} style={{ marginTop: `1em` }}>
            <Typography variant="h5">
              <Desc id="members" />
            </Typography>
          </Grid>
          {func_set.members.map((member, index) => (
            <Member
              id={id}
              member={member}
              index={index}
              controller={controller}
              write_access={write_access}
              setDialog={setDialog}
              Del={RemoveMember}
            />
          ))}
          <Grid item xs={12} md={2}>
            <Button color="primary" disabled={!write_access} onClick={() => AddMember()}>
              {t('add_member')}
            </Button>
          </Grid>
        </Grid>
        <Box style={saveButtonStyle()}>
          <Button
            disabled={!write_access}
            style={{ color: statusColors.bad }}
            onClick={() => Disable(id)}
          >
            {t('delete_function_set')}
          </Button>
        </Box>
      </Collapse>
    </SettingGroupGrid>
  );
}

function Member({ id, member, index, controller, write_access, setDialog, Del }) {
  let { t } = useTranslation();
  const funcIndexConfig = getMultiIoIndexConfig(useSnapshot(controller));
  const func = multiIOMapping[member.function];

  const member_str =
    (member.invert_logic ? '¬ ' : '  ') +
    Object.values(controller.zones)
      .map((z) => ({ value: parseInt(z.id), label: z.name }))
      .concat([
        { value: 0xfe, label: t('global') },
        { value: 0xff, label: t('undefined') }
      ])
      .find((x) => x.value === parseInt(member.source_zone))?.label +
    ' ⇒ ' +
    t(func) +
    (funcIndexConfig[member.function] ? ' [' + parseInt(member.func_index + 1) + ']' : '');

  return (
    <Grid item xs={12} md={2}>
      <ValueBox
        style={{
          display: 'flex',
          alignItems: 'center',
          columnGap: 1,
          position: 'relative',
          whiteSpace: 'normal',
          paddingRight: '2.2rem',
          color: member.enabled ? '' : 'darkGrey'
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={member_str}
        >
          {member_str}
        </span>
        <IconButton
          size="small"
          aria-label={t('edit_member')}
          disabled={!write_access}
          onClick={() => setDialog({ open: true, member: member, index: index, id: id })}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          disabled={!write_access}
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            padding: 4
          }}
          color="grey"
          onClick={() => Del(index)}
        >
          X
        </IconButton>
      </ValueBox>
    </Grid>
  );
}

function EditMemberDialog({
  dialog: data,
  setDialog: setData,
  controller,
  controls,
  write_access
}) {
  let { t } = useTranslation();

  const [member, setMember] = useState(data.member);
  useEffect(() => {
    setMember(data.member);
  }, [data]);

  // Readback and counters can change while the dialog is open.
  const controllerSnapshot = useSnapshot(controller);
  const funcIndexConfig = getMultiIoIndexConfig(controllerSnapshot);
  const controllerMember = controllerSnapshot.settings.user_func_sets?.[data.id]?.members?.[data.index];

  const save = () => {
    if (!write_access) return;
    controls.setValue(`${data.id}.members[${data.index}]`, member);
    setData({ open: false, member: undefined, index: -1, id: '' });
  };

  return (
    <DialogWithEnterAction
      open={data.open && write_access}
      onEnterAction={save}
      enterActionDisabled={!write_access}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('edit_member')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} columns={{ xs: 6, md: 12 }}>
          <Grid item xs={12} md={6}>
            <Typography gutterBottom>
              <Desc id="source_zone" />
            </Typography>
            <GenericSelect
              disabled={!write_access}
              onChange={(evt) => setMember({ ...member, source_zone: evt.target.value })}
              values={Object.values(controller.zones)
                .map((z) => ({ value: parseInt(z.id), label: z.name }))
                .concat([
                  { value: 0xfe, label: t('global') },
                  { value: 0xff, label: t('undefined') }
                ])}
              value={member?.source_zone}
              controller_value={controllerMember?.source_zone}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography gutterBottom>
              <Desc id="function" />
            </Typography>
            <MultiIOSelect
              parent={{ ...controller, id: '254' }}
              controller={controller}
              value={member?.function ?? ''}
              controller_value={controllerMember?.function}
              type={-1} // make `digital_out` and `digital_in` options availible
              hw_types={['digital_out', 'digital_in']}
              onChange={(evt, _) => setMember({ ...member, function: evt.target.value })}
              disabled={!write_access}
              no_label={false}
            />

            {funcIndexConfig[member?.function] && (
              <>
                <br />
                <br />
                <MultiIOSelectIndex
                  count={getMultiIoIndexCount(controllerSnapshot, controllerSnapshot.zones[member.source_zone], member.function)}
                  value={member?.func_index ?? 0}
                  controller_value={controllerMember?.func_index}
                  onChange={(evt, _) => setMember({ ...member, func_index: evt.target.value })}
                  disabled={!write_access}
                  {...(funcIndexConfig[member?.function].mapping && {
                    mapping: GetIndexMapping(funcIndexConfig[member?.function].mapping, controller, member.source_zone)
                  })}
                />
              </>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography gutterBottom>
              <Desc id="invert_logic" />
            </Typography>
            <MuiSwitch
              disabled={!write_access}
              checked={member?.invert_logic}
              controller_value={controllerMember?.invert_logic}
              onClick={(event) => setMember({ ...member, invert_logic: event.target.checked })}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography gutterBottom>
              <Desc id="enabled" />
            </Typography>
            <MuiSwitch
              disabled={!write_access}
              checked={member?.enabled}
              controller_value={controllerMember?.enabled}
              onClick={(event) => setMember({ ...member, enabled: event.target.checked })}
              color="primary"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button disabled={!write_access} onClick={() => save()} color="primary">
          {t('auto_save_ok')}
        </Button>
        <Button
          onClick={() => setData({ open: false, member: undefined, index: -1, id: '' })}
          color="secondary"
        >
          {t('cancel')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}
